// src/pages/Register.js

import React, { useState } from 'react';
import { createUserWithEmailAndPassword } from 'firebase/auth';
import { auth, db } from '../firebase';
import { doc, setDoc } from 'firebase/firestore';
import { useNavigate, Link } from 'react-router-dom';
import WalletsAPI from '../api/WalletsAPI';
import useRateLimit from '../hooks/useRateLimit';
import {
  Box,
  Button,
  TextField,
  Typography,
  InputAdornment,
  IconButton,
  Stepper,
  Step,
  StepLabel,
  Alert,
  Chip,
  LinearProgress,
} from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';
import {
  PersonOutlined,
  EmailOutlined,
  LockOutlined,
  VisibilityOutlined,
  VisibilityOffOutlined,
  CheckCircleOutlined,
  ArrowForwardOutlined,
  ArrowBackOutlined,
  AutoAwesomeOutlined,
  Telegram,
} from '@mui/icons-material';

// Password Strength Indicator
function PasswordStrength({ password }) {
  const calculateStrength = () => {
    let strength = 0;
    if (password.length >= 8) strength++;
    if (password.match(/[a-z]/) && password.match(/[A-Z]/)) strength++;
    if (password.match(/[0-9]/)) strength++;
    if (password.match(/[^a-zA-Z0-9]/)) strength++;
    return strength;
  };

  const strength = calculateStrength();
  const strengthLabels = ['Weak', 'Fair', 'Good', 'Strong'];
  const strengthColors = ['#DC2626', '#F59E0B', '#10B981', '#059669'];

  return (
    <Box sx={{ mt: 1 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
        <Typography variant="caption" sx={{ color: 'var(--text-secondary)' }}>
          Password Strength
        </Typography>
        <Typography
          variant="caption"
          sx={{
            color: strengthColors[strength - 1] || 'var(--text-secondary)',
            fontWeight: 600,
          }}
        >
          {password ? strengthLabels[strength - 1] || 'Too Weak' : ''}
        </Typography>
      </Box>
      <LinearProgress
        variant="determinate"
        value={(strength / 4) * 100}
        sx={{
          height: 4,
          borderRadius: 2,
          bgcolor: 'var(--bg-secondary)',
          '& .MuiLinearProgress-bar': {
            bgcolor: strengthColors[strength - 1] || 'var(--text-secondary)',
          },
        }}
      />
    </Box>
  );
}

// Feature Chip Component
function FeatureChip({ label, icon }) {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.8 }}
      animate={{ opacity: 1, scale: 1 }}
      whileHover={{ scale: 1.05 }}
    >
      <Chip
        icon={icon}
        label={label}
        sx={{
          bgcolor: 'var(--bg-secondary)',
          border: '1px solid var(--border-primary)',
          color: 'var(--text-primary)',
          '& .MuiChip-icon': {
            color: 'var(--primary-blue)',
          },
        }}
      />
    </motion.div>
  );
}

export default function Register() {
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  // Rate limiting - 3 attempts per 30 minutes for registration
  const { canAttempt, attemptAction, remainingAttempts } = useRateLimit('register', 3, 30 * 60 * 1000);
  
  // Check rate limit before registration attempt
  const checkRateLimit = () => {
    if (!canAttempt) {
      setError(`Too many registration attempts. Please try again in 30 minutes. (${remainingAttempts} attempts remaining)`);
      return false;
    }
    return true;
  };

  // Form state
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    password: '',
    confirmPassword: '',
    telegramUsername: '',
  });

  const [fieldErrors, setFieldErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const steps = ['Personal Info', 'Account Security', 'Complete Setup'];

  const validateStep = (step) => {
    const errors = {};

    if (step === 0) {
      if (!formData.fullName) {
        errors.fullName = 'Name is required';
      } else if (formData.fullName.length < 2) {
        errors.fullName = 'Name must be at least 2 characters';
      }
    }

    if (step === 1) {
      if (!formData.email) {
        errors.email = 'Email is required';
      } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
        errors.email = 'Email is invalid';
      }

      if (!formData.password) {
        errors.password = 'Password is required';
      } else if (formData.password.length < 6) {
        errors.password = 'Password must be at least 6 characters';
      }

      if (!formData.confirmPassword) {
        errors.confirmPassword = 'Please confirm your password';
      } else if (formData.password !== formData.confirmPassword) {
        errors.confirmPassword = 'Passwords do not match';
      }
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(activeStep)) {
      setActiveStep((prev) => prev + 1);
    }
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
  };

  const handleInputChange = (field) => (e) => {
    setFormData({ ...formData, [field]: e.target.value });
    setFieldErrors({ ...fieldErrors, [field]: '' });
  };

  const handleRegister = async () => {
    if (!validateStep(1)) return;
    
    // Check rate limit
    if (!checkRateLimit()) return;

    setLoading(true);
    setError('');

    try {
      // Record the registration attempt
      attemptAction();
      const result = await createUserWithEmailAndPassword(auth, formData.email, formData.password);
      const user = result.user;

      const userData = {
        name: formData.fullName,
        email: formData.email,
        role: 'customer',
        cards: [],
        createdAt: new Date().toISOString(),
      };
      
      // Add Telegram username if provided
      if (formData.telegramUsername && formData.telegramUsername.trim()) {
        userData.telegramUsername = formData.telegramUsername.trim();
      }
      
      await setDoc(doc(db, 'users', user.uid), userData);

      // Register user in the Wallets system
      try {
        await WalletsAPI.registerUserAdmin(formData.email, formData.fullName);
        // Log removed for security
      } catch (walletError) {
        // Error logging removed for security
        // Continue with registration even if wallet registration fails
      }

      setActiveStep(2);
      
      // Success animation before redirect
      setTimeout(() => {
        navigate('/dashboard');
      }, 2000);
    } catch (err) {
      // Error logging removed for security
      setError(
        err.code === 'auth/email-already-in-use'
          ? 'An account with this email already exists'
          : 'Failed to create account. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  };

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
          >
            <Box sx={{ mb: 3 }}>
              <Typography variant="h5" sx={{ mb: 1, fontWeight: 700 }}>
                Let's get started
              </Typography>
              <Typography variant="body2" sx={{ color: 'var(--text-secondary)' }}>
                First, tell us your name
              </Typography>
            </Box>

            <TextField
              fullWidth
              label="Full Name"
              value={formData.fullName}
              onChange={handleInputChange('fullName')}
              error={!!fieldErrors.fullName}
              helperText={fieldErrors.fullName}
              inputProps={{ maxLength: 50 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PersonOutlined sx={{ color: 'var(--text-secondary)' }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                mb: 3,
                '& .MuiOutlinedInput-root': {
                  bgcolor: 'var(--bg-secondary)',
                  '&:hover fieldset': {
                    borderColor: 'var(--primary-blue)',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: 'var(--primary-blue)',
                  },
                },
              }}
            />

            <TextField
              fullWidth
              label="Telegram Username (Optional)"
              value={formData.telegramUsername}
              onChange={handleInputChange('telegramUsername')}
              placeholder="@username"
              helperText="Enter your Telegram username for support (optional)"
              inputProps={{ maxLength: 50 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Telegram sx={{ color: 'var(--text-secondary)' }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  bgcolor: 'var(--bg-secondary)',
                  '&:hover fieldset': {
                    borderColor: 'var(--primary-blue)',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: 'var(--primary-blue)',
                  },
                },
              }}
            />

            <Box sx={{ mt: 4 }}>
              <Typography variant="body2" sx={{ mb: 2, color: 'var(--text-secondary)' }}>
                You'll get access to:
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                <FeatureChip label="Card Management" icon={<CheckCircleOutlined />} />
                <FeatureChip label="Real-time Tracking" icon={<CheckCircleOutlined />} />
                <FeatureChip label="Secure Platform" icon={<CheckCircleOutlined />} />
              </Box>
            </Box>
          </motion.div>
        );

      case 1:
        return (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
          >
            <Box sx={{ mb: 3 }}>
              <Typography variant="h5" sx={{ mb: 1, fontWeight: 700 }}>
                Secure your account
              </Typography>
              <Typography variant="body2" sx={{ color: 'var(--text-secondary)' }}>
                Choose a strong password to protect your account
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <TextField
                fullWidth
                label="Email Address"
                type="email"
                value={formData.email}
                onChange={handleInputChange('email')}
                error={!!fieldErrors.email}
                helperText={fieldErrors.email}
                inputProps={{ maxLength: 100 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <EmailOutlined sx={{ color: 'var(--text-secondary)' }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    bgcolor: 'var(--bg-secondary)',
                    '&:hover fieldset': {
                      borderColor: 'var(--primary-blue)',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: 'var(--primary-blue)',
                    },
                  },
                }}
              />

              <Box>
                <TextField
                  fullWidth
                  label="Password"
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={handleInputChange('password')}
                  error={!!fieldErrors.password}
                  helperText={fieldErrors.password}
                  inputProps={{ maxLength: 50 }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LockOutlined sx={{ color: 'var(--text-secondary)' }} />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                          sx={{ color: 'var(--text-secondary)' }}
                        >
                          {showPassword ? <VisibilityOffOutlined /> : <VisibilityOutlined />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      bgcolor: 'var(--bg-secondary)',
                      '&:hover fieldset': {
                        borderColor: 'var(--primary-blue)',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: 'var(--primary-blue)',
                      },
                    },
                  }}
                />
                {formData.password && <PasswordStrength password={formData.password} />}
              </Box>

              <TextField
                fullWidth
                label="Confirm Password"
                type={showConfirmPassword ? 'text' : 'password'}
                value={formData.confirmPassword}
                onChange={handleInputChange('confirmPassword')}
                error={!!fieldErrors.confirmPassword}
                helperText={fieldErrors.confirmPassword}
                inputProps={{ maxLength: 50 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockOutlined sx={{ color: 'var(--text-secondary)' }} />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        edge="end"
                        sx={{ color: 'var(--text-secondary)' }}
                      >
                        {showConfirmPassword ? <VisibilityOffOutlined /> : <VisibilityOutlined />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    bgcolor: 'var(--bg-secondary)',
                    '&:hover fieldset': {
                      borderColor: 'var(--primary-blue)',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: 'var(--primary-blue)',
                    },
                  },
                }}
              />
            </Box>
          </motion.div>
        );

      case 2:
        return (
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ type: "spring", stiffness: 100 }}
          >
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.2, type: "spring", stiffness: 100 }}
              >
                <CheckCircleOutlined
                  sx={{
                    fontSize: 80,
                    color: 'var(--accent-success)',
                    filter: 'drop-shadow(0 0 20px rgba(16, 185, 129, 0.5))',
                    mb: 3,
                  }}
                />
              </motion.div>

              <Typography variant="h4" sx={{ mb: 2, fontWeight: 700 }}>
                Welcome to CardsPay!
              </Typography>
              
              <Typography variant="body1" sx={{ color: 'var(--text-secondary)', mb: 4 }}>
                Your account has been created successfully.
                <br />
                Redirecting you to your dashboard...
              </Typography>

              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
              >
                <AutoAwesomeOutlined
                  sx={{
                    fontSize: 32,
                    color: 'var(--primary-blue)',
                  }}
                />
              </motion.div>
            </Box>
          </motion.div>
        );

      default:
        return null;
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: 'var(--bg-primary)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        p: 2,
      }}
    >
      {/* Animated Background */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'radial-gradient(circle at 30% 20%, rgba(59, 130, 246, 0.1) 0%, transparent 50%)',
          pointerEvents: 'none',
        }}
      />

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        style={{ width: '100%', maxWidth: 520, position: 'relative', zIndex: 1 }}
      >
        <Box
          className="glass-card"
          sx={{
            p: { xs: 4, md: 6 },
            borderRadius: 4,
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          {/* Progress Bar */}
          <LinearProgress
            variant="determinate"
            value={(activeStep / (steps.length - 1)) * 100}
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: 4,
              bgcolor: 'var(--bg-secondary)',
              '& .MuiLinearProgress-bar': {
                background: 'linear-gradient(90deg, var(--primary-blue), var(--primary-sky))',
              },
            }}
          />

          {/* Logo */}
          <Box sx={{ textAlign: 'center', mb: 4, mt: 2 }}>
            <Link to="/" style={{ textDecoration: 'none' }}>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 900,
                  fontFamily: 'Space Grotesk, sans-serif',
                  background: 'linear-gradient(45deg, var(--primary-blue), var(--primary-sky))',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                CardsPay
              </Typography>
            </Link>
          </Box>

          {/* Stepper */}
          {activeStep < 2 && (
            <Stepper
              activeStep={activeStep}
              sx={{
                mb: 4,
                '& .MuiStepLabel-root .Mui-completed': {
                  color: 'var(--primary-blue)',
                },
                '& .MuiStepLabel-root .Mui-active': {
                  color: 'var(--primary-blue)',
                },
                '& .MuiStepLabel-label': {
                  color: 'var(--text-secondary)',
                },
              }}
            >
              {steps.map((label) => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>
          )}

          {/* Error Alert */}
          <AnimatePresence>
            {error && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
              >
                <Alert
                  severity="error"
                  onClose={() => setError('')}
                  sx={{ mb: 3 }}
                >
                  {error}
                </Alert>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Step Content */}
          <AnimatePresence mode="wait">
            {renderStepContent()}
          </AnimatePresence>

          {/* Navigation Buttons */}
          {activeStep < 2 && (
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
              <Button
                onClick={handleBack}
                disabled={activeStep === 0}
                startIcon={<ArrowBackOutlined />}
                sx={{
                  color: 'var(--text-secondary)',
                  '&:disabled': {
                    opacity: 0,
                  },
                }}
              >
                Back
              </Button>

              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                <Button
                  onClick={activeStep === 1 ? handleRegister : handleNext}
                  disabled={loading}
                  endIcon={activeStep === 1 ? null : <ArrowForwardOutlined />}
                  sx={{
                    px: 4,
                    py: 1.5,
                    background: 'linear-gradient(45deg, var(--primary-blue), var(--primary-sky))',
                    color: 'white',
                    fontWeight: 600,
                    boxShadow: '0 8px 32px rgba(59, 130, 246, 0.3)',
                    '&:hover': {
                      boxShadow: '0 12px 48px rgba(59, 130, 246, 0.4)',
                    },
                    '&:disabled': {
                      background: 'var(--bg-secondary)',
                      color: 'var(--text-secondary)',
                    },
                  }}
                >
                  {loading ? (
                    <motion.div
                      animate={{ rotate: 360 }}
                      transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                    >
                      <AutoAwesomeOutlined />
                    </motion.div>
                  ) : activeStep === 1 ? (
                    'Create Account'
                  ) : (
                    'Continue'
                  )}
                </Button>
              </motion.div>
            </Box>
          )}

          {/* Sign In Link */}
          {activeStep < 2 && (
            <Typography
              variant="body2"
              sx={{
                textAlign: 'center',
                mt: 3,
                color: 'var(--text-secondary)',
              }}
            >
              Already have an account?{' '}
              <Link
                to="/login"
                style={{
                  color: 'var(--primary-blue)',
                  textDecoration: 'none',
                  fontWeight: 600,
                }}
              >
                Sign in
              </Link>
            </Typography>
          )}
        </Box>
      </motion.div>
    </Box>
  );
}
